import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders() {
  const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtUGX(n: number) {
  return 'UGX ' + Math.round(n).toLocaleString('en-UG');
}

interface Category {
  id: string;
  name: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  _count?: { items: number };
}

interface MenuItem {
  id: string;
  name: string;
  description?: string;
  priceUgx: number;
  categoryId: string;
  category?: { name: string };
  isAvailable: boolean;
  is86d: boolean;
  preparationTime?: number;
  imageUrl?: string;
}

function Skeleton({ h = 36 }: { h?: number }) {
  return (
    <div style={{
      height: h, borderRadius: 8,
      background: 'var(--surface2)', animation: 'pulse 1.5s infinite',
    }} />
  );
}

function StatusBadge({ item }: { item: MenuItem }) {
  if (item.is86d) return <span className="badge badge-red">86'd</span>;
  if (item.isAvailable) return <span className="badge badge-green">Available</span>;
  return <span className="badge badge-gray">Unavailable</span>;
}

function CategoryModal({
  initial,
  onClose,
  onSave,
  isSaving,
}: {
  initial?: Partial<Category>;
  onClose: () => void;
  onSave: (data: { name: string; description: string; sortOrder: number }) => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [sortOrder, setSortOrder] = useState(initial?.sortOrder ?? 0);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h2 className="modal-title">{initial?.id ? 'Edit Category' : 'New Category'}</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="field">
            <label className="field-label">Name *</label>
            <input
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Main Course"
            />
          </div>
          <div className="field">
            <label className="field-label">Description</label>
            <input
              className="input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional description"
            />
          </div>
          <div className="field">
            <label className="field-label">Display Order</label>
            <input
              className="input"
              type="number"
              value={sortOrder}
              onChange={e => setSortOrder(parseInt(e.target.value) || 0)}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => { if (name.trim()) onSave({ name: name.trim(), description, sortOrder }); }}
            disabled={!name.trim() || isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ItemModal({
  initial,
  categories,
  onClose,
  onSave,
  isSaving,
}: {
  initial?: Partial<MenuItem>;
  categories: Category[];
  onClose: () => void;
  onSave: (data: { name: string; description: string; priceUgx: number; categoryId: string; preparationTime: number }) => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [price, setPrice] = useState(initial?.priceUgx ?? 0);
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? (categories[0]?.id ?? ''));
  const [preparationTime, setPreparationTime] = useState(initial?.preparationTime ?? 15);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h2 className="modal-title">{initial?.id ? 'Edit Item' : 'New Menu Item'}</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="field">
            <label className="field-label">Name *</label>
            <input
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Rolex"
            />
          </div>
          <div className="field">
            <label className="field-label">Category *</label>
            <select
              className="input"
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
            >
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Price (UGX) *</label>
            <input
              className="input"
              type="number"
              value={price}
              onChange={e => setPrice(parseInt(e.target.value) || 0)}
              placeholder="e.g. 15000"
            />
          </div>
          <div className="field">
            <label className="field-label">Description</label>
            <input
              className="input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="field">
            <label className="field-label">Prep Time (minutes)</label>
            <input
              className="input"
              type="number"
              value={preparationTime}
              onChange={e => setPreparationTime(parseInt(e.target.value) || 0)}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => { if (name.trim() && categoryId && price > 0) onSave({ name: name.trim(), description, priceUgx: price, categoryId, preparationTime }); }}
            disabled={!name.trim() || !categoryId || price <= 0 || isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MenuPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'items' | 'categories'>('items');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categoryModal, setCategoryModal] = useState<{ open: boolean; editing?: Category }>({ open: false });
  const [itemModal, setItemModal] = useState<{ open: boolean; editing?: MenuItem }>({ open: false });

  const { data: categories = [], isLoading: catsLoading } = useQuery<Category[]>({
    queryKey: ['menu-categories'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/menu/categories`, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed to load categories');
      return j.data;
    },
  });

  const { data: items = [], isLoading: itemsLoading } = useQuery<MenuItem[]>({
    queryKey: ['menu-items', selectedCategory],
    queryFn: async () => {
      const url = selectedCategory === 'all'
        ? `${API_BASE}/v1/menu/items`
        : `${API_BASE}/v1/menu/items?categoryId=${selectedCategory}`;
      const res = await fetch(url, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed to load items');
      // API returns categories-with-nested-items; flatten to a single item list
      const raw = j.data as Array<{ name: string; items?: MenuItem[] } | MenuItem>;
      if (Array.isArray(raw) && raw.length > 0 && 'items' in raw[0]!) {
        return (raw as Array<{ name: string; items?: MenuItem[] }>).flatMap(cat =>
          (cat.items ?? []).map(item => ({ ...item, category: { name: cat.name } }))
        );
      }
      return raw as MenuItem[];
    },
  });

  const saveCategory = useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: object }) => {
      const res = await fetch(
        id ? `${API_BASE}/v1/menu/categories/${id}` : `${API_BASE}/v1/menu/categories`,
        { method: id ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(data) },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Save failed');
      return j.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['menu-categories'] }); setCategoryModal({ open: false }); },
  });

  const saveItem = useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: object }) => {
      const res = await fetch(
        id ? `${API_BASE}/v1/menu/items/${id}` : `${API_BASE}/v1/menu/items`,
        { method: id ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(data) },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Save failed');
      return j.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['menu-items'] }); setItemModal({ open: false }); },
  });

  const toggle86 = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/v1/menu/items/${id}/86`, { method: 'PATCH', headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menu-items'] }),
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/v1/menu/categories/${id}`, { method: 'DELETE', headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Delete failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['menu-categories'] }),
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
            {items.length} items across {categories.length} categories
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => setCategoryModal({ open: true })}>+ Category</button>
          <button className="btn btn-primary" onClick={() => setItemModal({ open: true })}>+ Menu Item</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {(['items', 'categories'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '6px 18px', borderRadius: 8, fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
              background: activeTab === tab ? 'var(--surface)' : 'transparent',
              color: activeTab === tab ? 'var(--text)' : 'var(--text-3)',
              border: 'none', cursor: 'pointer',
              boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.12s',
            }}
          >
            {tab === 'items' ? 'Menu Items' : 'Categories'}
          </button>
        ))}
      </div>

      {activeTab === 'items' && (
        <>
          {/* Category filter chips */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            {(['all', ...categories.map(c => c.id)] as string[]).map(id => {
              const label = id === 'all' ? 'All' : categories.find(c => c.id === id)?.name ?? id;
              const active = selectedCategory === id;
              return (
                <button
                  key={id}
                  onClick={() => setSelectedCategory(id)}
                  style={{
                    padding: '5px 14px', borderRadius: 99, fontSize: 12, fontWeight: active ? 600 : 400,
                    background: active ? 'var(--accent)' : 'var(--surface)',
                    color: active ? '#fff' : 'var(--text-2)',
                    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                    cursor: 'pointer', transition: 'all 0.12s',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {itemsLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} h={144} />)}
            </div>
          ) : items.length === 0 ? (
            <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
              No menu items yet. Add your first item.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {items.map(item => (
                <div key={item.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</p>
                      <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 2 }}>{item.category?.name ?? '—'}</p>
                    </div>
                    <p style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 13, flexShrink: 0, fontFamily: 'var(--font-mono)' }}>{fmtUGX(item.priceUgx)}</p>
                  </div>
                  {item.description && (
                    <p style={{ color: 'var(--text-2)', fontSize: 12, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}>{item.description}</p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <StatusBadge item={item} />
                      {item.preparationTime && (
                        <span className="badge badge-gray">{item.preparationTime}m</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => toggle86.mutate(item.id)}
                        style={{
                          padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: item.is86d ? 'var(--green)' : 'var(--red)',
                        }}
                      >
                        {item.is86d ? 'Restore' : '86'}
                      </button>
                      <button
                        onClick={() => setItemModal({ open: true, editing: item })}
                        style={{
                          padding: '3px 8px', borderRadius: 6, fontSize: 12,
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--text-3)',
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'categories' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {catsLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={48} />)}
            </div>
          ) : categories.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
              No categories yet.
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Description</th>
                    <th style={{ textAlign: 'center' }}>Order</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat => (
                    <tr key={cat.id}>
                      <td style={{ fontWeight: 500, color: 'var(--text)' }}>{cat.name}</td>
                      <td style={{ color: 'var(--text-2)' }}>{cat.description ?? '—'}</td>
                      <td style={{ textAlign: 'center', color: 'var(--text-2)' }}>{cat.sortOrder}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${cat.isActive ? 'badge-green' : 'badge-gray'}`}>
                          {cat.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setCategoryModal({ open: true, editing: cat })}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => { if (confirm(`Delete "${cat.name}"?`)) deleteCategory.mutate(cat.id); }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {categoryModal.open && (
        <CategoryModal
          initial={categoryModal.editing}
          onClose={() => setCategoryModal({ open: false })}
          onSave={data => saveCategory.mutate({ id: categoryModal.editing?.id, data })}
          isSaving={saveCategory.isPending}
        />
      )}

      {itemModal.open && (
        <ItemModal
          initial={itemModal.editing}
          categories={categories}
          onClose={() => setItemModal({ open: false })}
          onSave={data => saveItem.mutate({ id: itemModal.editing?.id, data })}
          isSaving={saveItem.isPending}
        />
      )}
    </div>
  );
}
